import type { FieldNamesMarkedBoolean, FieldValues } from 'react-hook-form'
import type { PortalUpdateAddressInput } from '@/graphql/generated/graphql'
import type { AddressEditItem } from '@/shared/schemas/addressEditSchema'

/** Mapa de campos modificados de `react-hook-form` (`formState.dirtyFields`). */
export type DirtyMap<T extends FieldValues> = Partial<Readonly<FieldNamesMarkedBoolean<T>>>

/**
 * Convierte las direcciones del form en el array que espera
 * `PortalUpdateAddressInput`. Por fila:
 *
 * - nueva borrada (sin `id`)       → se omite (nunca existió)
 * - existente borrada (`_deleted`) → `{ id, shouldDelete: true }`
 * - nueva (sin `id`)               → crear (campos, sin `id`)
 * - existente (con `id`)           → `{ id, ...campos }` (siempre, con su `id`)
 *
 * El `id` SIEMPRE viaja en las existentes para que el backend actualice (no
 * cree) la dirección. La decisión de "¿cambió algo?" se toma en el llamador a
 * nivel de sección (`dirty.addresses`), no por fila — la marca por fila de
 * react-hook-form no es fiable con los resets en cascada de `LocationFields`.
 *
 * El llamador debe agregar `addresses` al input **solo si el resultado tiene
 * length > 0**; un array vacío podría interpretarse como "borrar todas".
 */
export function diffAddressesForUpdate(
  addresses: AddressEditItem[],
): PortalUpdateAddressInput[] {
  const out: PortalUpdateAddressInput[] = []

  for (const a of addresses) {
    if (a._deleted) {
      if (a.id != null) out.push({ id: a.id, shouldDelete: true })
      continue
    }
    const lat = a.coordinateLatitude?.trim()
    const lng = a.coordinateLongitude?.trim()
    out.push({
      ...(a.id != null ? { id: a.id } : {}),
      street1: a.street1,
      city: a.city,
      countryId: a.countryId,
      provinceId: a.provinceId,
      municipalityId: a.municipalityId,
      // Solo se envían si tienen valor (v1 no limpia una coordenada ya fijada).
      ...(lat ? { coordinateLatitude: lat } : {}),
      ...(lng ? { coordinateLongitude: lng } : {}),
      isMain: a.isMain,
    })
  }

  return out
}
